import styled from '@emotion/styled';
import React from 'react';

import Accordion from '../../../ComponentLibrary/oc-accordion';
import { H3, H5 } from '../../../OC/oc-h';
import { altText } from '../../Shingles/imageAlts';
import ContactMethods from '../EstimationForHomeOwner/ContactMethods';
import { presentMoneyRange } from '../functions/sharedFunctions';

// Styled Components
const SuccessContainer = styled.div`
  background-color: #f8f8f8;
  display: inline-block;
  padding: 40px;
  position: relative;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 10px;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media screen and (max-width: 768px) {
    gap: 16px;
  }

  p {
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

const H2Container = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 600px) {
    flex-direction: column;
    justify-content: center;
    gap: 8px;
  }
`;

const StyledH2 = styled.p`
  margin-bottom: 0px;
  font-family: Oswald, sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 34px;
  font-size: 32px;
}
`;

const ColorStyledH2 = styled(StyledH2)`
  color: #d40f7d;
`;

const DetailSection = styled.div`
  border-bottom: 2px solid #000000;
  display: inline-block;
  padding-bottom: 48px;
  position: relative;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding-bottom: 14px;
  }

  &.table-container {
    border-bottom: none;
    padding-bottom: 0;
    padding-top: 0;
  }

  &:last-of-type {
    border-bottom: 0;
  }

  h4 {
    margin-bottom: 24px;

    @media screen and (max-width: 768px) {
      margin-bottom: 16px;
    }
  }
`;

const NextSection = styled.div`
  border-top: 2px solid #000000;
  display: inline-block;
  margin-top: 64px;
  position: relative;
  width: 100%;

  @media screen and (max-width: 768px) {
    margin-top: 30px;
  }

  &.table-container {
    border-bottom: none;
    padding-bottom: 0;
    padding-top: 0;
  }

  &:last-of-type {
    border-bottom: 0;
  }

  h3 {
    margin-top: 24px;
    margin-bottom: 16px;
    @media screen and (max-width: 768px) {
      margin-top: 16px;
    }
  }
`;

const AccordionContainer = styled.div`
  margin-bottom: 16px;
`;

const Footer = styled.div`
  border-top: 2px solid black;
  display: flex;
  gap: 22px;
  margin-top: 64px;
  padding-top: 24px;

  @media screen and (max-width: 768px) {
    margin-top: 30px;
  }

  img {
    width: 52px;
  }

  h3:first-of-type {
    margin-bottom: 24px;
  }

  p {
    margin-top: 0;
  }
`;

const H6 = styled.h6`
  margin-bottom: 8px;

  sup {
    font-size: 12px;
    font-weight: 400;
  }
`;

const Note = styled.p`
  color: #585858;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  margin-bottom: 4px;
  text-transform: none;
  font-family: OCRoboto;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const AccordionNote = styled.p`
  margin: 0;
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const labelHeader = styled.div`
  font-weight: 700;
  font-size: 16px;
  text-transform: uppercase;
`;

const ProductsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;

  @media screen and (max-width: 400px) {
    flex-direction: column;
  }
`;

const ConfigContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 94%;
  border-top: 2px solid black;

  @media screen and (max-width: 400px) {
    width: 100%;
  }

  p {
    padding: 0;
    margin-bottom: 10px;
    margin-top: 0px;
  }

  p:last-of-type {
    padding: 0;
    margin: 0;
  }

  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
  }

  h3 {
    margin-top: 16px;
    margin-bottom: 0px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 2px solid black;
  width: 94%;

  @media screen and (max-width: 400px) {
    width: 100%;
  }

  h3 {
    margin-top: 16px;
    margin-bottom: 16px;
  }
`;

const ColorImage = styled.img`
  width: 100%;
  object-fit: initial;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const QuotingFormSuccess = ({ configuration, formData, estimateValues }) => {
  const pitchFactor = configuration[`${formData.complexity}-pitch-factor`]
    ? configuration[`${formData.complexity}-pitch-factor`]
    : configuration.pitchFactor || 1;
  const contractorPhone = configuration.companyPhone;
  const contractorEmail = configuration.communicationsEmail || configuration.leadsEmail;
  const roofingComponentData = [
    configuration['ice-water-barrier'],
    configuration.underlayment,
    configuration['exhaust-ventilation'],
    configuration['hip-ridge-shingles'],
    configuration['starter-shingles'],
  ];

  const roofingComponentDisplay = () => {
    let found = false;
    roofingComponentData.forEach(data => {
      if (data.display) found = true;
    });
    return found;
  };

  const renderWhatsIncludedList = () => {
    const renderWarranty = () => {
      const { warrantySelected } = configuration;
      if (warrantySelected && warrantySelected.display) {
        return (
          <>
            <H6>
              {warrantySelected.title}
              <sup>†</sup>
            </H6>
            <AccordionNote>{warrantySelected.description}</AccordionNote>
          </>
        );
      }
      return null;
    };

    const renderQuoteDetails = () => {
      const { quoteDetails } = configuration;
      if (quoteDetails) {
        return quoteDetails.map(detail => {
          if (detail.display) {
            return (
              <>
                <H6>{detail.title}</H6>
                <AccordionNote>{detail.description}</AccordionNote>
              </>
            );
          }
          return null;
        });
      }
      return null;
    };

    return (
      <>
        {renderWarranty()}
        {renderQuoteDetails()}
      </>
    );
  };

  return (
    <SuccessContainer>
      <HeadingContainer>
        <H2Container>
          <StyledH2>Approximate Roofing Budget</StyledH2>
          <ColorStyledH2>
            {presentMoneyRange(estimateValues.min_estimate, estimateValues.max_estimate, false)}*
          </ColorStyledH2>
        </H2Container>
        <p>
          Based on the information you provided, we have calculated an approximate budget for a new roof on your home.
          Contact us with the information below to receive an official quote.
        </p>
        <DetailSection>
          <ContactMethods contractorPhone={contractorPhone} contractorEmail={contractorEmail} includeEmailSelf />
        </DetailSection>
      </HeadingContainer>
      <ProductsWrapper>
        <ConfigContainer>
          <H3>PRODUCTS</H3>
          <H6>Shingle Line</H6>
          <p>{formData['shingle-color'][2]}</p>
          <H6>Shingle Color</H6>
          <p>
            {formData['shingle-color'][0]
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </p>
          {roofingComponentDisplay() && (
            <>
              <H6>Total Protection Roofing System®^</H6>
              {roofingComponentData.map(data => {
                if (!data.display) return;
                const { product } = data;
                const name = product?.proper_name;
                return <p>{name}</p>;
              })}
            </>
          )}
        </ConfigContainer>
        <ImageContainer>
          <H3>SHINGLE PREVIEW</H3>
          <ColorImage
            src={formData['shingle-color'][3]}
            alt={altText(formData['shingle-color'][0], 'swatch', 'en', formData['shingle-color'][1])}
          />
        </ImageContainer>
      </ProductsWrapper>
      <NextSection>
        <H3>NEXT STEPS</H3>
        {configuration.warrantySelected?.display == true ||
        configuration.quoteDetails.some(detail => detail.display === true) ? (
          <>
            <AccordionNote>
              Contact us to receive an official quote that includes the following details and more.
            </AccordionNote>
            <AccordionContainer>
              <Accordion
                label="What's included in the official quote"
                labelClassName={labelHeader}
                color="#F8F8F8"
                headColor="#E6E6E6"
              >
                {renderWhatsIncludedList()}
              </Accordion>
            </AccordionContainer>
          </>
        ) : (
          <AccordionNote>Contact us to receive an official quote</AccordionNote>
        )}
        <Note>
          * This is not an official quote, and price is subject to change. The approximate budget is based on the
          dimensions entered into the calculator.
        </Note>
        <Note>
          ^ The Total Protection Roofing System® excludes non-Owens Corning™ Roofing Products such as flashing,
          fasteners, and wood decking.
        </Note>
        <Note>† See actual warranty for complete details, limitations, and requirements.</Note>
        <Note>
          We strive to accurately reproduce all photographs of shingles in literature and for onscreen viewing. The
          roofing product colors you see are as accurate as technology allows. We suggest that you view a roofing
          display or several shingles to get a better idea of the actual color. To accurately judge your shingle and
          color choice, we recommend that you view it on an actual roof with a pitch similar to your own roof prior to
          making your final color selection.
        </Note>
      </NextSection>

      <Footer>
        <article>
          <img
            src="https://imagecdn.owenscorning.com/ocimages/image/upload/v1575575892/meganav/oc-logo.svg"
            alt="Owens Corning Logo"
          />
        </article>
        <article>
          <H3>owens corning products</H3>
          <H5 font="body">More Than Just a Roof&#174;</H5>
          <p>
            Owens Corning is more than just a trusted name, we are a market leader in roofing shingles, underlayment and
            components products. Our systems look better, last longer and offer more choices to perfectly fit your
            project.
          </p>
        </article>
      </Footer>
    </SuccessContainer>
  );
};

export default QuotingFormSuccess;
